import { useContext, useEffect, useMemo, useReducer, useState } from 'react'
import { WppDivider, WppTypography } from '@wppopen/components-library-react'
import appContext from 'context/appContext'
import {
  IdValuePair,
  SelectFilterComponentData,
  SelectFilterOptionPayload,
  SetSortByPayload,
} from 'reducers/GenericFiltersReducer/GenericFiltersReducer.d'
import {
  columnStructure,
  defaultGroupIdFilter,
  defaultProductIdFilter,
  defaultProjectNameFilter,
  defaultReferenceNameFilter,
  getFilterElementInitFields,
  JobWithClientAndBrand,
  tableFilterReducerEmptyDataMessage,
  tableFilters,
} from '../Home/utils'
import {
  filterReducer,
  getFilterReducerEmptyState,
  initialiseFilterOptions,
  removeAllFilters,
  selectFilterOption,
  setAreDependantFieldsReady,
  setIsPreselectComplete,
  setSortBy,
} from 'reducers/GenericFiltersReducer/GenericFiltersReducer'
import Filters from 'components/Filters/Filters'
import type {
  SortOrder,
  JobsListData,
  JobSortableAttribute,
  JobsReqParams,
  JobsListParams,
  ConfigListData,
} from '@contracts'
import {
  combineSearchParams,
  getClientAndBrand,
  createCsApiUrlFromEndpoint,
  getDefaultPaginationSearchParams,
  getTypeKey,
  crossReferenceArray,
  getType,
  pluralise,
} from 'utils'
import useApi from 'hooks/useApi'
import { ManualUpload } from 'components/Modals'
import Banner from 'components/Banner'
import DownloadAllJobsButton from 'components/DownloadAllButton/DownloadAllJobsButton'
import Table from 'components/table'
import TableMetaDataRow from 'components/table/TableMetaDataRow'
import { SortingDetails } from 'components/table/table-types'
import { useNavigate } from 'react-router-dom'
import useToast from 'hooks/useToast'
import usePermissions from 'hooks/usePermissions'
import useInitialiser from 'hooks/useInitialiser'
import MenuListPopUp, { type MenuItems } from 'components/MenuListPopUp/MenuListPopUp'
import {
  paginatedTableReducer,
  getPaginatedTableReducerEmptyState,
  setTaskPosition,
  setRowData,
  setTotalItemCount,
} from 'reducers/PaginatedTableReducer/PaginatedTableReducer'
import { DEFAULT_API_ITEM_LIMITS, FILTER_IDS, PDP_GENERATOR_API_HEADERS } from 'utils/constants'
import type { RowClickedEvent } from 'ag-grid-community'
import type { ExtractElementType, singleBrandClientV5, singleClientV5 } from 'utils/utils.d'
import homePageStyles from '../Home/HomePage.module.scss'
import styles from './TaskManagerPage.module.scss'

// manual
function TaskManagerPage() {
  const navigate = useNavigate()
  const [isManualUploadOpen, setIsManualUploadOpen] = useState(false)
  const {
    clientsData,
    importedClients,
    clientApiError,
    preservedFilterSelection,
    isClientApiLoading,
    updatePreservedFilterSelectionWithReducer,
    setTotalNumberJobIds,
    setLoadedJobIds,
    setPreservedSortingSelection,
    currentClients,
    setCurrentClients,
    configApi,
  } = useContext(appContext)
  const { toastDetails, setToastDetails } = useToast(true)
  const { isInitialising: areCoreApisInitialising } = useInitialiser({})
  const { clientsWithPermissions, isGetPermissionsLoading } = usePermissions({
    permissionSubset: [
      'PDP_APP_GET_JOBS',
      'PDP_APP_GET_JOBS_EXPORT',
      'PDP_APP_POST_JOBS_IMPORTS',
      'PDP_APP_POST_JOBS_IMPORTS_BY',
    ],
  })

  const [clientsWithGetJobs, clientsWithGetExportJobs, clientsWithPostImportJobs, clientsWithPostImportSingleJob] =
    clientsWithPermissions
  const clientsWithPostUploadJobs = !!clientsWithPostImportJobs?.length && !!clientsWithPostImportSingleJob?.length

  //   REDUCERS
  const [tableFilterReducerState, tableFilterReducerDispatch] = useReducer(
    filterReducer,
    getFilterReducerEmptyState(FILTER_IDS.TASK_MANAGER, tableFilters),
  )
  const [tableReducerState, tableReducerDispatch] = useReducer(
    paginatedTableReducer<JobWithClientAndBrand>,
    getPaginatedTableReducerEmptyState<JobWithClientAndBrand>(),
  )

  // ############################## API LOGIC ##############################
  const getJobsApiMethods = useApi<JobsListData>(createCsApiUrlFromEndpoint('jobs'))
  const {
    fetchData: getJobs,
    data: getJobsData,
    isLoading: isGetJobsLoading,
    error: getJobsError,
    url: getJobsUrl,
    setUrl: setGetJobsUrl,
  } = getJobsApiMethods

  // Load retailers in via config API
  const { data: configData } = configApi

  const getJobExportApiMethods = useApi<string>('jobs/export', {}, 0, 'csv')

  // ############################## LOCAL STATE & VARIABLES ##############################
  // The locally controlled state for the current clients, needs to be kept in sync with the preserved filter state
  const [isPageInitialised, setIsPageInitialised] = useState<boolean>(false)
  const [shouldShowDownloadError, setShouldShowDownloadError] = useState<boolean>(false)
  const [shouldAppend, setShouldAppend] = useState<boolean>(false)
  const [sortDetails, setSortDetails] = useState<SetSortByPayload<JobSortableAttribute> | null>(null)
  const [areNoTasksFoundForFilters, setAreNoTasksFoundForFilters] = useState<boolean>(false)
  // [CMCST-2593]: if the user is allowed to get jobs, the table should start initialising, if not, then no need to initialise
  const [isTableInitialising, setIsTableInitialising] = useState(
    clientsWithGetJobs ? !!clientsWithGetJobs.length : true,
  )
  const preservedFilterObjects = preservedFilterSelection[tableFilterReducerState.reducerId]
  const preservedClientFilters = preservedFilterObjects?.find(({ filterId }) => filterId === 'clients') || null
  const preservedBrandFilters = preservedFilterObjects?.find(({ filterId }) => filterId === 'brands')
  const preservedRetailerFilters = preservedFilterObjects?.find(({ filterId }) => filterId === 'retailers') || null

  const activeClientFilterString = JSON.stringify(tableFilterReducerState.index['clients'].activeFilters)
  const preservedClientFilterString = JSON.stringify(preservedClientFilters?.activeFilters)
  // ############################## FILTER LOGIC ##############################

  // initialise filters with client data after the clients API has returned
  useEffect(() => {
    if (!importedClients?.length) return

    // Get the data for initialising the client filter ELEMENT
    const { currentFilterComponent: clientFilterComponent } = getFilterElementInitFields<singleClientV5>(
      'client_name',
      importedClients,
      preservedClientFilters,
    )

    // when revisiting the page, maybe the api response may have changed, so only select the preserved clients if they are still in the API response
    const nonStalePreservedClients = preservedClientFilters
      ? crossReferenceArray(importedClients, preservedClientFilters?.activeFilters as IdValuePair[])
      : []

    // update the filter state with the preserved client data (pre-selecting previously selected options)
    tableFilterReducerDispatch(initialiseFilterOptions('clients', clientFilterComponent))

    if (!nonStalePreservedClients.length) {
      tableFilterReducerDispatch(setIsPreselectComplete(true))
      return
    }

    // Get the initialisation data for the brands filter element from the data received in the clients API response
    // and any preserved brands selected from previous interactions on the page
    const { currentFilterComponent: brandFilterComponent } = getFilterElementInitFields<singleBrandClientV5>(
      'brand_name',
      nonStalePreservedClients
        .map(({ brands }) => brands)
        .filter((brands): brands is NonNullable<typeof brands> => Boolean(brands))
        .flat(),
      preservedBrandFilters!,
    )

    // Iniitialise the brands filter reducer
    tableFilterReducerDispatch(initialiseFilterOptions('brands', brandFilterComponent))
    tableFilterReducerDispatch(setIsPreselectComplete(true))
  }, [importedClients])

  // Set the current clients in reducer state
  useEffect(() => {
    // If no clients data is received from the API, exit
    if (!importedClients) return
    tableFilterReducerDispatch(setAreDependantFieldsReady(false))

    const clientFilterElementState = tableFilterReducerState.index['clients'] as SelectFilterComponentData

    // If the user has manually selected a client, use the existing filter state, otherwise use the preserved filter state to preselect any clients
    if (clientFilterElementState?.activeFilters?.length) {
      // if active filters for client (e.g. selecting client in select box)
      setCurrentClients(crossReferenceArray(importedClients, clientFilterElementState.activeFilters as IdValuePair[]))
    } else {
      // set client from filter preservation (e.g. navigating away and back), or fallback to first client in list (e.g. first load)
      setCurrentClients(
        crossReferenceArray(importedClients, (preservedClientFilters?.activeFilters as IdValuePair[]) || []),
      )
    }
  }, [activeClientFilterString, preservedClientFilterString])

  // remove all filters & update brands options on client change
  useEffect(() => {
    if (!importedClients) return

    const { currentFilterComponent: brandFilterComponent } = getFilterElementInitFields<singleBrandClientV5>(
      'brand_name',
      currentClients?.length
        ? currentClients.flatMap(({ brands }) => brands).filter((brand): brand is NonNullable<typeof brand> => !!brand)
        : importedClients
            ?.flatMap(({ brands }) => brands)
            .filter((brand): brand is NonNullable<typeof brand> => !!brand) || [],
      preservedBrandFilters!,
    )

    // Initialise the new 'brand' select filter options after a change in client
    tableFilterReducerDispatch(initialiseFilterOptions('brands', brandFilterComponent))
    let shouldClearBrands = false

    if (isPageInitialised && !tableFilterReducerState.areDependantFieldsReady) {
      // This should remove all of the active filters for brands
      tableFilterReducerDispatch(removeAllFilters('brands'))
      updatePreservedFilterSelectionWithReducer(tableFilterReducerState)
      shouldClearBrands = !!brandFilterComponent.activeFilters.length
    } else {
      setIsPageInitialised(true)
    }

    // dependant fields have updated, globalFilterReducer is ready
    if (!shouldClearBrands) tableFilterReducerDispatch(setAreDependantFieldsReady(true))
  }, [importedClients, currentClients, JSON.stringify(preservedClientFilters?.activeFilters)])

  // Update the filter selection in the context for persistance of filters accross different pages
  useEffect(() => {
    if (isPageInitialised && tableFilterReducerState.areDependantFieldsReady)
      updatePreservedFilterSelectionWithReducer(tableFilterReducerState)
  }, [tableFilterReducerState.urlQueryParams])

  // pre-select filters from the preserved filter state (excluding clients as that pre-selection is handled above)
  useEffect(() => {
    preservedFilterObjects
      // clients and brands preselection are handled above
      ?.filter(({ filterId }) => filterId !== 'clients' && filterId !== 'brands')
      .forEach(selectedFilter => {
        const matchedObject = tableFilterReducerState.index[selectedFilter.filterId]
        if (!matchedObject) return
        // if the filter type found in the preserved filter state matches a filter type on the page, pre-select the filter
        tableFilterReducerDispatch(
          selectFilterOption(matchedObject.type, {
            filterObject: matchedObject,
            newFilterValues: selectedFilter.activeFilters,
          } as SelectFilterOptionPayload),
        )
      })
    tableFilterReducerDispatch(setIsPreselectComplete(true))
  }, [])

  // pre-select filters from the preserved filter state (excluding clients as that pre-selection is handled above)
  useEffect(() => {
    if (!isPageInitialised) return
    preservedFilterSelection[tableFilterReducerState.reducerId]?.forEach(selectedFilter => {
      const matchedObject = tableFilterReducerState.index[selectedFilter.filterId]
      if (!matchedObject) return
      tableFilterReducerDispatch(
        selectFilterOption(matchedObject.type, {
          filterObject: matchedObject,
          newFilterValues: selectedFilter.activeFilters,
        } as SelectFilterOptionPayload),
      )
    })
    tableFilterReducerDispatch(setIsPreselectComplete(true))
  }, [isPageInitialised])

  // create the query parameters from filter reducers
  const jobApiSearchParams: URLSearchParams = useMemo(() => {
    const defaultPaginationSearchParams = getDefaultPaginationSearchParams(DEFAULT_API_ITEM_LIMITS.JOBS_API)
    const apiUrlSearchParams = combineSearchParams(
      defaultPaginationSearchParams,
      tableFilterReducerState.urlQueryParams,
    )

    return apiUrlSearchParams
  }, [tableFilterReducerState.urlQueryParams])

  // update job api url with the query parameters from the reducers
  useEffect(() => {
    if (!importedClients) return
    const newUrl = new URL(getJobsUrl)
    newUrl.search = jobApiSearchParams.toString()
    if (!tableFilterReducerState.isPreselectComplete || !tableFilterReducerState.areDependantFieldsReady) return
    setGetJobsUrl(newUrl)
  }, [jobApiSearchParams, tableFilterReducerState.isPreselectComplete, tableFilterReducerState.areDependantFieldsReady])

  const updateCurrentSortingSelection = () => {
    setPreservedSortingSelection({
      sortBy:
        (tableFilterReducerState.urlQueryParams.get(getTypeKey<JobsReqParams>('sort_by')) as JobSortableAttribute) ||
        null,
      sortOrder:
        (tableFilterReducerState.urlQueryParams.get(getTypeKey<JobsReqParams>('sort_order')) as SortOrder) || null,
    })
  }

  // Fetch data
  useEffect(() => {
    if (
      areCoreApisInitialising ||
      isGetJobsLoading ||
      !getJobsUrl.searchParams.get('position') ||
      !tableFilterReducerState.areDependantFieldsReady ||
      !clientsWithGetJobs?.length
    )
      return
    if (tableFilterReducerState.isPreselectComplete) updateCurrentFilterSelectionInContext()
    updateCurrentSortingSelection()
    const client_ids = getJobsUrl.searchParams.get(getTypeKey<JobsListParams>('clients'))
    const requestOptions: any = {
      method: 'GET',
      headers: {
        [PDP_GENERATOR_API_HEADERS.CLIENT_IDS_HEADER]: client_ids || null,
      },
    }
    if (!client_ids) delete requestOptions.headers[PDP_GENERATOR_API_HEADERS.CLIENT_IDS_HEADER]
    getJobs(getJobsUrl, requestOptions).catch(err => console.error(err))
  }, [getJobsUrl, areCoreApisInitialising])

  // ############################## FILTER LOGIC END ##############################

  // ############################## TABLE LOGIC ##############################

  const updateCurrentFilterSelectionInContext = () => {
    setShouldShowDownloadError(false)
    updatePreservedFilterSelectionWithReducer(tableFilterReducerState)
  }

  const setPosition = (position: number = 0) => {
    const newUrl = new URL(getJobsUrl)
    newUrl.searchParams.set('position', position.toString())
    setGetJobsUrl(newUrl)
  }

  const handleLoadMore = () => {
    if (!getJobsData) return
    const { position, item_count } = getJobsData
    const newPosition = (position || 0) + (item_count || 0)
    tableReducerDispatch(setTaskPosition(newPosition))
    setPosition(newPosition)
    setShouldAppend(true)
  }

  const processTaskData = () => {
    if (!getJobsData) return
    const { jobs, position, item_count, total_item_count } = getJobsData

    const newJobs: JobWithClientAndBrand[] =
      jobs?.map(job => {
        // find the client and brand name from the client data
        const { client_id, brand_id } = job
        const getClientAndBrandName = getClientAndBrand(clientsData, client_id, brand_id)

        return {
          ...job,
          ...getClientAndBrandName,
        }
      }) || []

    tableReducerDispatch(setRowData({ rowData: !!clientsWithGetJobs?.length ? newJobs : [], shouldAppend }))
    tableReducerDispatch(setTaskPosition(shouldAppend ? position! + item_count! : 0))
    tableReducerDispatch(setTotalItemCount(total_item_count!))
  }

  // update retailers options on config data load
  useEffect(() => {
    const { currentFilterComponent } = getFilterElementInitFields<ExtractElementType<ConfigListData['retailers']>>(
      'name',
      configData?.retailers || [],
      preservedRetailerFilters,
    )
    tableFilterReducerDispatch(initialiseFilterOptions('retailers', currentFilterComponent))
    if (isPageInitialised) {
      tableFilterReducerDispatch(removeAllFilters('retailers'))
      updatePreservedFilterSelectionWithReducer(tableFilterReducerState)
    }
    setIsPageInitialised(true)
    tableFilterReducerDispatch(setAreDependantFieldsReady(true))
  }, [configData])

  // handle API response
  useEffect(() => {
    if (isGetJobsLoading || !getJobsData || getJobsError) return
    processTaskData()
    setShouldAppend(false)
    setIsTableInitialising(false)
  }, [isGetJobsLoading])

  useEffect(() => {
    setLoadedJobIds(tableReducerState.rowData.map(({ _id }) => _id || 'N/A'))
    setAreNoTasksFoundForFilters(!!tableFilterReducerState.orderedFilters.length && !tableReducerState.rowData.length)
    setTotalNumberJobIds(tableReducerState.totalItemCount)
  }, [tableReducerState.rowData])

  useEffect(() => {
    tableFilterReducerDispatch(setSortBy(sortDetails))
  }, [sortDetails])

  const handleTableRowClick = (event: RowClickedEvent) => {
    const { data } = event
    const { _id } = data as JobWithClientAndBrand
    navigate(`/task/${_id}`)
  }

  const refreshHandler = () => {
    setShouldShowDownloadError(false)
    if (!getJobsData) return
    const newPosition = 0
    tableReducerDispatch(setTaskPosition(newPosition))
    setPosition()
    if (!tableReducerState.rowData.length) setIsTableInitialising(true)
  }

  const menuItems: MenuItems = useMemo(() => {
    const returnMenuItems: MenuItems = []
    if (clientsWithPostUploadJobs) returnMenuItems.push({ label: 'Upload', onClick: () => setIsManualUploadOpen(true) })
    return returnMenuItems
  }, [clientsWithPostUploadJobs])

  // TODO: update to adopt useInitialiser as the sole initialisation logic in WPPOPENCOS-572
  const isPageLoading = isClientApiLoading || isGetPermissionsLoading || (!importedClients && !clientApiError)
  const isTableLoading = isTableInitialising || (isGetJobsLoading && !tableReducerState.rowData?.length)
  const isInitialising = isPageLoading || isTableLoading

  return (
    <>
      <div className={styles.actions_section}>
        <WppTypography type="xl-heading" tag="h1">
          Tasks
        </WppTypography>
        <Banner
          text={toastDetails?.message}
          type={toastDetails?.type}
          onBannerClick={toastDetails?.onBannerClick}
          shouldBannerShow={!!toastDetails}
          onClose={() => setToastDetails(undefined)}
        />

        <MenuListPopUp menuItems={menuItems} isInitialising={isInitialising} isDisabled={isInitialising} />
      </div>

      <div className={homePageStyles.header__container}>
        <Filters
          reducerState={tableFilterReducerState}
          reducerDispatch={tableFilterReducerDispatch}
          isTargetApiLoading={isGetJobsLoading}
          shouldDisplayPills
          filtersId="tasks"
          permissions={isPageLoading ? null : { canGetEntity: !!clientsWithGetJobs?.length }}
          initialisation={{
            isInitialising: isPageLoading,
            skeletonCount: 6,
          }}
          groupInputs
          groupInputKeys={getType<{ id: keyof JobsListParams; value: string }[]>([
            { id: defaultProjectNameFilter.id as keyof JobsListParams, value: defaultProjectNameFilter.labelText },
            { id: defaultGroupIdFilter.id as keyof JobsListParams, value: defaultGroupIdFilter.labelText },
            {
              id: defaultReferenceNameFilter.id as keyof JobsListParams,
              value: defaultReferenceNameFilter.labelText,
            },
            { id: defaultProductIdFilter.id as keyof JobsListParams, value: defaultProductIdFilter.labelText },
          ])}
        />
      </div>
      {!!clientsWithGetExportJobs?.length && <WppDivider />}
      <section>
        <TableMetaDataRow
          text={`Showing ${tableReducerState.totalItemCount} ${pluralise('result', tableReducerState.totalItemCount)}`}
          refreshHandler={refreshHandler}
          isInitialising={isInitialising}
          isLoading={isGetJobsLoading}
          // if the page is loading, we want the loading skeletons to appear, if not, we check the permissions to see if the refresh button should be rendered
          permissions={{ canRefresh: isPageLoading || !!clientsWithGetJobs?.length }}
        >
          {(isPageLoading || !!clientsWithGetExportJobs?.length) && (
            <DownloadAllJobsButton
              fetchUrl={getJobsUrl}
              jobExportApiReq={getJobExportApiMethods}
              fetchJobData={getJobs}
              showDownloadError={shouldShowDownloadError}
              setShouldShowDownloadError={setShouldShowDownloadError}
              disabled={isGetJobsLoading || isInitialising}
            />
          )}
        </TableMetaDataRow>

        <Banner
          text="Error: Unable to download tasks"
          shouldBannerShow={shouldShowDownloadError}
          onClose={() => setShouldShowDownloadError(false)}
          type="error"
        />
        {!areNoTasksFoundForFilters || isInitialising ? (
          <Table
            loading={isGetJobsLoading}
            loadedRows={tableReducerState.itemCount}
            totalRows={tableReducerState.totalItemCount}
            rowData={tableReducerState.rowData}
            columnDefs={columnStructure}
            onLoadMore={handleLoadMore}
            onRowClicked={handleTableRowClick}
            onSortingUpdate={(sort: SortingDetails | undefined) => {
              if (!sort) {
                setSortDetails(null)
                return
              }

              setSortDetails({
                column: sort.column as JobSortableAttribute,
                sort: `${sort.sort as 'asc' | 'desc'}ending`,
              })
            }}
            // TODO: add condition for get job
            isDrillable
            isInitialising={isInitialising}
          />
        ) : (
          /* if is not loading, here is data (meaning api was called) and there is projects */
          <div className={homePageStyles.no_table_content}>
            <WppTypography tag="p" type="s-body">
              {tableFilterReducerEmptyDataMessage}
            </WppTypography>
          </div>
        )}
      </section>

      {isManualUploadOpen && (
        <ManualUpload
          shouldModalOpen={isManualUploadOpen}
          onCancel={() => setIsManualUploadOpen(false)}
          onSuccessCb={() => {
            setIsManualUploadOpen(false)
            setToastDetails({
              message: 'Content uploaded successfully',
              type: 'success',
            })
          }}
          onErrorCb={() => {
            setToastDetails({
              message: 'Error: Unable to download tasks',
              type: 'error',
            })
          }}
        />
      )}
    </>
  )
}

export default TaskManagerPage
